import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQ {
    question: string;
    answer: string;
}

const FAQS: FAQ[] = [
    {
        question: 'What is a Murrah buffalo and how can I identify it?',
        answer:
            'The Murrah buffalo is one of the best dairy buffalo breeds in India, well known for its high milk yield, strong genetics, and adaptability to different climatic conditions.\n\n' +
            'Identification features of Murrah Buffalo:\n' +
            '• Jet-black skin color\n' +
            '• Short, tightly curved horns (spiral shape)\n' +
            '• Broad head with a prominent forehead\n' +
            '• Well-developed udder\n' +
            '• Strong and compact body structure\n' +
            '• High milk-producing capacity',
    },
    {
        question: 'How can I identify a female Murrah buffalo in India?',
        answer:
            'A female Murrah buffalo can be identified using the following characteristics:\n\n' +
            '• Deep black skin without patches\n' +
            '• Medium to large body size\n' +
            '• Small, thick, and tightly curved horns\n' +
            '• Well-shaped udder with evenly placed teats\n' +
            '• Calm temperament and good feeding response\n' +
            '• High milk yield potential\n\n' +
            'All female Murrah buffaloes provided through Animal Kart are carefully selected and verified by experts.',
    },
    {
        question:
            'After purchasing, how does delivery work and why is there a 6-month gap for the next buffalo unit?',
        answer:
            'When you purchase 1 unit, you receive:\n\n' +
            '• 2 Murrah buffaloes\n' +
            '• 2 calves\n\n' +
            'Delivery cycle explanation:\n' +
            '• 1 Murrah buffalo + 1 calf will be transported to the farm within 1 month\n' +
            '• The remaining 1 Murrah buffalo + 1 calf will be imported and delivered within 6 months\n\n' +
            'This structured cycle ensures:\n' +
            '• Continuous monthly revenue\n' +
            '• Proper quarantine and health monitoring\n' +
            '• Reduced operational risk\n' +
            '• Better long-term profitability\n\n' +
            'This delivery cycle is designed to maximize income stability and overall profit.',
    },
    {
        question: 'Why is CPF (Cattle Protection Fund) mandatory?',
        answer:
            'CPF is mandatory to ensure complete protection and long-term security of your livestock investment.\n\n' +
            'CPF benefits include:\n' +
            '• Full medical care in case of health issues\n' +
            '• Replacement of buffalo in case of critical loss\n' +
            '• Artificial insemination support to ensure the birth of female Murrah calves only\n' +
            '• Continuous health monitoring\n' +
            '• Live CCTV access to monitor your buffaloes in real time\n\n' +
            'CPF safeguards your asset and ensures uninterrupted income generation.',
    },
    {
        question: 'Why do I need to purchase at least 1 unit?',
        answer:
            '1 unit includes:\n' +
            '• 2 Murrah buffaloes\n' +
            '• 2 calves\n\n' +
            'Purchasing 1 full unit is mandatory because:\n' +
            '• It ensures higher and more stable income\n' +
            '• It supports proper milk production cycles\n' +
            '• It enables eligibility for CPF benefits\n' +
            '• It provides long-term profit scalability\n\n' +
            'Purchasing only 1 buffalo + 1 calf does not generate sufficient income or profit and is therefore not supported.',
    },
    {
        question: 'What is Animal Kart?',
        answer:
            'Animal Kart is a digital platform that allows you to purchase units of high-quality livestock and earn returns through professional management.',
    },
    {
        question: 'How do I purchase a unit?',
        answer:
            'Browse the buffalo list, select a breed, choose the number of units, and complete the payment using Online or Manual payment options.',
    },
    {
        question: 'What is CPF?',
        answer:
            'CPF (Cattle Protection Fund) is an protection for your investment, covering risks associated with livestock health.',
    },
    {
        question: 'How does the referral program work?',
        answer:
            'Once you purchase your first unit, your referral feature is unlocked. You can then refer friends by entering their contact details in the "Refer & Earn" section.',
    },
    {
        question: 'What rewards do I get for referrals?',
        answer:
            'You earn coins for every unit your friend purchases (excluding CPF). These coins can be accumulated to unlock more units.',
    },
    {
        question: 'Are the referral rewards cumulative?',
        answer:
            'Yes! If your friend purchases multiple units at once or over time, you receive the 5% reward for each unit they buy.',
    },
    {
        question: 'How can I track my referrals?',
        answer:
            'You can track all your successful referrals and earned coins in the "Refer & Earn" section under "Track my referrals".',
    },
    {
        question: 'What are the payment options?',
        answer:
            'We support Manual payment (Direct Bank Transfer).',

    },
    {
        question: 'Can I sell or transfer my units?',
        answer:
            'Yes, units can be transferred or sold within the platform according to the terms of service after a minimum holding period.',
    },
    {
        question: 'How do I contact support?',
        answer:
            'If you have any issues, you can "Raise a Request" in the HelpDesk, or use our WhatsApp support for quick assistance.',
    },
];

const FAQItem: React.FC<{ item: FAQ }> = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);

    const renderContent = (text: string) => {
        const lines = text.split('\n');
        const elements: React.ReactNode[] = [];
        let listItems: string[] = [];
        let keyCounter = 0;

        const flushList = () => {
            if (listItems.length > 0) {
                elements.push(
                    <ul key={`list-${keyCounter++}`} className="my-2.5 ml-5 p-0 list-outside text-slate-500 list-disc">
                        {listItems.map((li, idx) => (
                            <li key={idx} className="mb-2 leading-normal">
                                {li.substring(1).trim()}
                            </li>
                        ))}
                    </ul>
                );
                listItems = [];
            }
        };

        lines.forEach((line) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('•')) {
                listItems.push(trimmed);
            } else {
                flushList();
                if (trimmed) {
                    elements.push(
                        <p key={`p-${keyCounter++}`} className="mb-2.5 text-slate-500 leading-relaxed whitespace-pre-line">
                            {trimmed}
                        </p>
                    );
                }
            }
        });
        flushList();

        return elements;
    };

    return (
        <div 
            className={`bg-white border rounded-lg overflow-hidden transition-all duration-200 cursor-pointer ${
                isOpen 
                ? 'border-[#35d1f5] shadow-md shadow-[#35d1f5]/10' 
                : 'border-gray-400 hover:border-gray-500 hover:shadow-sm'
            }`} 
            onClick={() => setIsOpen(!isOpen)}
        >
            <div className="px-5 py-3 flex justify-between items-center font-semibold text-slate-800 bg-white">
                <span className="text-[1.05rem]">{item.question}</span>
                {isOpen ? <ChevronUp size={20} className="text-[#35d1f5]" /> : <ChevronDown size={20} />}
            </div>
            <div 
                className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0,1,0,1)] bg-slate-50 px-5 ${
                    isOpen ? 'max-h-[2000px] py-5 border-t border-slate-200' : 'max-h-0'
                }`}
            >
                {renderContent(item.answer)}
            </div>
        </div>
    );
};

const Support: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
    });

    const isFormValid = formData.firstName.trim().length >= 2 && /^[^0-9]+$/.test(formData.firstName) &&
        formData.lastName.trim().length >= 1 && /^[^0-9]+$/.test(formData.lastName) &&
        /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(formData.email) &&
        /^\d{10}$/.test(formData.phone) &&
        formData.message.trim().length >= 10;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let { name, value } = e.target;

        if (name === 'phone') {
            // Only allow digits and max 10
            value = value.replace(/\D/g, '').slice(0, 10);
        } else if (name === 'email') {
            // Automatically convert to lowercase
            value = value.toLowerCase();
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Reset form
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            message: ''
        });
    };

    return (
        <div className="max-w-[800px] mx-auto px-5 py-10 font-['Inter',sans-serif] text-slate-700 bg-white h-screen overflow-y-auto box-border">
            <div className="text-center mb-10 border-b-2 border-slate-200 pb-5">
                <h1 className="text-4xl sm:text-[2.5rem]  text-black mb-2.5 leading-tight">Contact Support</h1>
                <p className="text-slate-500 text-[1.1rem]">We are here to help. Check our FAQs or fill out the form below.</p>
            </div>

            <div className="max-w-[800px] mx-auto mb-10">
                <h2 className="text-center mb-6 text-slate-800 text-[1.8rem] ">Frequently Asked Questions</h2>
                <div className="flex flex-col gap-2">
                    {FAQS.map((faq, index) => (
                        <FAQItem key={index} item={faq} />
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl p-[30px] shadow-md border border-slate-200 mb-10">
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="flex-1">
                            <div className="mb-5">
                                <label htmlFor="firstName" className="block font-semibold mb-2 text-slate-600">First Name</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    className="w-full p-2 border border-slate-300 rounded-lg text-base transition-all duration-200 bg-slate-50 focus:border-[#35d1f5] focus:outline-none focus:ring-2 focus:ring-[#35d1f5]/15 focus:bg-white"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex-1">
                            <div className="mb-5">
                                <label htmlFor="lastName" className="block font-semibold mb-2 text-slate-600">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    className="w-full p-2 border border-slate-300 rounded-lg text-base transition-all duration-200 bg-slate-50 focus:border-[#35d1f5] focus:outline-none focus:ring-2 focus:ring-[#35d1f5]/15 focus:bg-white"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mb-5">
                        <label htmlFor="email" className="block font-semibold mb-2 text-slate-600">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="w-full p-2 border border-slate-300 rounded-lg text-base transition-all duration-200 bg-slate-50 focus:border-[#35d1f5] focus:outline-none focus:ring-2 focus:ring-[#35d1f5]/15 focus:bg-white"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="mb-5">
                        <label htmlFor="phone" className="block font-semibold mb-2 text-slate-600">Phone Number</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className="w-full p-2 border border-slate-300 rounded-lg text-base transition-all duration-200 bg-slate-50 focus:border-[#35d1f5] focus:outline-none focus:ring-2 focus:ring-[#35d1f5]/15 focus:bg-white"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="mb-5">
                        <label htmlFor="message" className="block font-semibold mb-2 text-slate-600">How can we help you?</label>
                        <textarea
                            id="message"
                            name="message"
                            className="w-full p-2 border border-slate-300 rounded-lg text-base transition-all duration-200 bg-slate-50 focus:border-[#35d1f5] focus:outline-none focus:ring-2 focus:ring-[#35d1f5]/15 focus:bg-white resize-y min-h-[100px]"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Describe your issue or question..."
                            required
                        ></textarea>
                    </div>

                    <button
                        type="submit"
                        className="w-full p-2.5 bg-[#35d1f5] hover:bg-[#0ea5e9] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white rounded-lg text-[1.1rem] font-semibold transition-colors duration-200"
                        disabled={!isFormValid}
                    >
                        Send Message
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Support;
